/**
 * Mining-laser heat (spec §5.4, plan §2.9 `mining.json heat`). One `HeatState` per ship laser (a
 * `FractureState` is one, so a single struct can carry both). Heat rises with power while the beam fires, cools
 * when it is off, and at 1 forces the beam off for `overheatCooldownSeconds` while it keeps cooling.
 * Allocation-free (D-014).
 */
import type { MiningConfig } from '../data/m2Schemas'

export interface HeatState {
  /** 0..1; 1 = overheated. */
  heat: number
  /** Seconds of forced cooldown left (> 0 while overheated: the beam is off). */
  cooldown: number
}

export function createHeatState(): HeatState {
  return { heat: 0, cooldown: 0 }
}

/** `stepHeat` results (the runtime emits `SEV.overheat` / `SEV.cooled` on them). */
export const HEAT_EVENT = { none: 0, overheat: 1, cooled: 2 } as const
export type HeatEvent = (typeof HEAT_EVENT)[keyof typeof HEAT_EVENT]

/** The laser is in its forced cooldown (the beam must stay off). */
export function isOverheated(state: HeatState): boolean {
  return state.cooldown > 0
}

/**
 * Advances laser heat by `dt`. Firing (`beamOn`, ignored while overheated) adds
 * `ratePerPower × power / heatCapacityMult` per second; otherwise heat falls at `coolRate`. Reaching 1 starts the
 * `overheatCooldownSeconds` cooldown (returns `overheat`); its end returns `cooled`.
 */
export function stepHeat(
  state: HeatState,
  beamOn: boolean,
  power: number,
  dt: number,
  heatCapacityMult: number,
  cfg: Pick<MiningConfig, 'heat'>,
): HeatEvent {
  const h = cfg.heat
  if (state.cooldown > 0) {
    state.heat = Math.max(0, state.heat - h.coolRate * dt)
    state.cooldown -= dt
    if (state.cooldown > 0) return HEAT_EVENT.none
    state.cooldown = 0
    return HEAT_EVENT.cooled
  }
  if (beamOn && power > 0) {
    state.heat += (h.ratePerPower * power * dt) / heatCapacityMult
    if (state.heat >= 1) {
      state.heat = 1
      state.cooldown = h.overheatCooldownSeconds
      return HEAT_EVENT.overheat
    }
    return HEAT_EVENT.none
  }
  state.heat = Math.max(0, state.heat - h.coolRate * dt)
  return HEAT_EVENT.none
}
